import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BellRing, Play, MailX, MailCheck, Send } from "lucide-react";
import { toast } from "sonner";

export default function ReminderSettings() {
  const [s, setS] = useState(null);
  const [lead, setLead] = useState(14);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/reminders").then((r) => { setS(r.data); setLead(r.data.lead_days); });
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/reminders", { lead_days: Number(lead), enabled: s.enabled, ...patch });
      setS(data); setLead(data.lead_days);
      toast.success("Reminder settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reminders/run");
      toast.success(`Reminder check complete — ${data.created} new reminder(s) for ${data.managers} manager(s)${data.emailed ? `, ${data.emailed} emailed` : ""}`);
    } catch { toast.error("Could not run reminders"); }
    finally { setBusy(false); }
  };

  const testEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/email/test");
      toast.success(`Test email sent to you via Resend (id ${data.email_id || "ok"})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test email failed", { duration: 9000 }); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  const connected = s.email_provider === "resend";
  return (
    <div data-testid="reminder-settings" className="mt-4 pt-4 border-t border-slate-200">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0A2A5E]"><BellRing className="w-4 h-4 text-[#C7761A]" /> Reminders</div>
        <Switch data-testid="reminders-enabled" checked={s.enabled} onCheckedChange={(v) => save({ enabled: v })} disabled={busy} />
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-xs text-slate-600">Notify managers</span>
        <Input data-testid="reminder-lead-days" type="number" min={1} max={90} value={lead} onChange={(e) => setLead(e.target.value)} onBlur={() => Number(lead) !== s.lead_days && save({})} className="w-16 h-8 text-xs" />
        <span className="text-xs text-slate-600">days before each window opens</span>
      </div>
      <div data-testid="email-provider-status" className={`mt-2 text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md ${connected ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
        {connected ? <MailCheck className="w-3 h-3" /> : <MailX className="w-3 h-3" />}
        {connected ? <>Email via Resend · from <strong className="ml-1">{s.sender}</strong></> : <>In-app inbox only · email not connected (add RESEND_API_KEY) · sender will be <strong className="ml-1">{s.sender || "LUMIS HR App <info@lumishrsolutions.com>"}</strong></>}
      </div>
      <div className="flex items-center justify-end gap-2 mt-2">
        {connected && <Button size="sm" variant="outline" data-testid="test-email-btn" onClick={testEmail} disabled={busy} className="h-7 text-xs"><Send className="w-3 h-3 mr-1" /> Send test email</Button>}
        <Button size="sm" variant="outline" data-testid="run-reminders-btn" onClick={runNow} disabled={busy} className="h-7 text-xs border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]"><Play className="w-3 h-3 mr-1" /> Run check now</Button>
      </div>
    </div>
  );
}
